// Manual supplier notes, served by Workato recipes behind the cloud gateway.
//
// Plain Axios rather than `FCCUtil` on purpose: that throttles to three
// concurrent requests, so the notes read would queue behind the merchant
// `/details` call and land long after the rest of the page.
import { reactive } from 'vue'
import axios from 'axios'
import { API_ENDPOINTS } from '@/config/api.endpoints'
import type { AuthState } from '@/types/authState'
import type { SupplierNote, SupplierNoteAction } from '@/types/fcc'
import { ApiError } from '@/util/ApiError'
import { normalizeError } from '@/util/errorNormalizer'

export enum API_STATES {
  INITIAL = 'INITIAL',
  LOADING = 'LOADING',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR',
}

interface SupplierNotesApi {
  states: {
    GET_NOTES: API_STATES
    CREATE_NOTE: API_STATES
    UPDATE_NOTE: API_STATES
  }
  error: ApiError | null
  getNotes(authState: AuthState, merchantId: string): Promise<SupplierNote[]>
  createNote(
    authState: AuthState,
    merchantId: string,
    body: string,
  ): Promise<SupplierNote | null>
  updateNote(
    authState: AuthState,
    merchantId: string,
    noteId: number,
    body: string,
  ): Promise<SupplierNote | null>
  deleteNote(
    authState: AuthState,
    merchantId: string,
    noteId: number,
  ): Promise<void>
}

function authHeaders(authState: AuthState) {
  return {
    headers: {
      Authorization: `Bearer ${authState.user?.accessToken}`,
      'Content-Type': 'application/json',
    },
  }
}

/**
 * Map one note's lowercase wire keys to the UPPER_SNAKE_CASE the rest of FCC
 * uses, as reserve metrics does. Falls back to the uppercase key so a casing
 * change on the backend does not break the read. Null for a row with no usable
 * id or body.
 */
function normalizeNote(raw: Record<string, unknown>): SupplierNote | null {
  const pick = (low: string, up: string): unknown =>
    low in raw ? raw[low] : raw[up]

  const id = Number(pick('note_id', 'NOTE_ID'))
  const body = pick('body', 'BODY')
  if (!Number.isFinite(id) || typeof body !== 'string') return null

  const asString = (value: unknown): string | null =>
    value === null || value === undefined ? null : String(value)

  return {
    NOTE_ID: id,
    BODY: body,
    AUTHOR_EMAIL: asString(pick('author_email', 'AUTHOR_EMAIL')),
    CREATED_AT: asString(pick('created_at', 'CREATED_AT')) ?? '',
    UPDATED_AT: asString(pick('updated_at', 'UPDATED_AT')),
  }
}

/** Every endpoint answers `{ notes: [...] }` — the writes with one entry. */
function normalizeNotes(data: unknown): SupplierNote[] {
  const rows = (data as { notes?: unknown })?.notes
  if (!Array.isArray(rows)) return []
  return rows
    .filter(
      (row): row is Record<string, unknown> => !!row && typeof row === 'object',
    )
    .map(normalizeNote)
    .filter((note): note is SupplierNote => note !== null)
}

export const supplierNotesApi: SupplierNotesApi = reactive({
  states: {
    GET_NOTES: API_STATES.INITIAL,
    CREATE_NOTE: API_STATES.INITIAL,
    UPDATE_NOTE: API_STATES.INITIAL,
  },
  error: null,

  /** Every live note for the supplier, newest first, in one call. */
  getNotes(authState: AuthState, merchantId: string): Promise<SupplierNote[]> {
    supplierNotesApi.states.GET_NOTES = API_STATES.LOADING
    supplierNotesApi.error = null

    return axios
      .get(API_ENDPOINTS.FCC.SUPPLIER_NOTES(merchantId), authHeaders(authState))
      .then(({ data }) => {
        supplierNotesApi.states.GET_NOTES = API_STATES.COMPLETE
        return normalizeNotes(data)
      })
      .catch(err => {
        supplierNotesApi.states.GET_NOTES = API_STATES.ERROR
        const apiError = normalizeError(err)
        supplierNotesApi.error = apiError
        throw apiError
      })
  },

  /** Resolves to the created note, or null if the API answers without one. */
  createNote(
    authState: AuthState,
    merchantId: string,
    body: string,
  ): Promise<SupplierNote | null> {
    supplierNotesApi.states.CREATE_NOTE = API_STATES.LOADING
    supplierNotesApi.error = null

    return axios
      .post(
        API_ENDPOINTS.FCC.SUPPLIER_NOTES(merchantId),
        { body },
        authHeaders(authState),
      )
      .then(({ data }) => {
        supplierNotesApi.states.CREATE_NOTE = API_STATES.COMPLETE
        return normalizeNotes(data)[0] ?? null
      })
      .catch(err => {
        supplierNotesApi.states.CREATE_NOTE = API_STATES.ERROR
        const apiError = normalizeError(err)
        supplierNotesApi.error = apiError
        throw apiError
      })
  },

  updateNote(
    authState: AuthState,
    merchantId: string,
    noteId: number,
    body: string,
  ): Promise<SupplierNote | null> {
    return patchNote(authState, merchantId, noteId, 'EDIT', body).then(
      notes => notes[0] ?? null,
    )
  },

  /** A soft delete: the note keeps its history, it just stops being listed. */
  deleteNote(
    authState: AuthState,
    merchantId: string,
    noteId: number,
  ): Promise<void> {
    return patchNote(authState, merchantId, noteId, 'DELETE').then(
      () => undefined,
    )
  },
})

/**
 * Edit and delete are one PATCH differing only by `action`, so they share a
 * request path. They stay separate on the API surface so callers need not know.
 */
function patchNote(
  authState: AuthState,
  merchantId: string,
  noteId: number,
  action: SupplierNoteAction,
  body?: string,
): Promise<SupplierNote[]> {
  supplierNotesApi.states.UPDATE_NOTE = API_STATES.LOADING
  supplierNotesApi.error = null

  const payload: { action: SupplierNoteAction; body?: string } = { action }
  if (body !== undefined) payload.body = body

  return axios
    .patch(
      API_ENDPOINTS.FCC.SUPPLIER_NOTE(merchantId, noteId),
      payload,
      authHeaders(authState),
    )
    .then(({ data }) => {
      supplierNotesApi.states.UPDATE_NOTE = API_STATES.COMPLETE
      return normalizeNotes(data)
    })
    .catch(err => {
      supplierNotesApi.states.UPDATE_NOTE = API_STATES.ERROR
      const apiError = normalizeError(err)
      supplierNotesApi.error = apiError
      throw apiError
    })
}
